import { createContext, useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { empresasService } from "app/services";
import { toast } from "react-hot-toast";
import { ErrorMessage, Spinner } from "app/components";

const PaginasWebContext = createContext();

export function PaginasWebProvider({ children }) {
  const params = useParams();
  const navigate = useNavigate();

  const paginasWebQuery = useQuery({
    queryKey: ["empresa:get:paginas-web"],
    queryFn: () =>
      empresasService.getPaginasWeb({ empresaId: params.empresa_id }),
  });

  const paginaWebAddMutation = useMutation({
    queryKey: ["empresa:add:pagina-web", params.empresa_id],
    mutationFn: (data) =>
      toast.promise(empresasService.addPaginaWeb(data), {
        loading: "Agregando la dirección de página web...",
        success: (response) => response.statusText,
        error: (error) => error.response.statusText,
      }),
    onSuccess: async () => {
      navigate(".");

      return await paginasWebQuery.refetch();
    },
  });

  const paginaWebUpdateMutation = useMutation({
    queryKey: ["empresa:update:pagina-web"],
    mutationFn: (data) =>
      toast.promise(empresasService.updatePaginaWeb(data), {
        loading: "Actualizando la dirección de página web...",
        success: (response) => response.statusText,
        error: (error) => error.response.statusText,
      }),
    onSuccess: async () => {
      navigate(".");

      return await paginasWebQuery.refetch();
    },
  });

  const paginaWebDeleteMutation = useMutation({
    queryKey: ["empresa:delete:pagina-web"],
    mutationFn: (data) =>
      toast.promise(empresasService.deletePaginaWeb(data), {
        loading: "Eliminando la dirección de página web...",
        success: (response) => response.statusText,
        error: (error) => error.response.statusText,
      }),
    onSuccess: async () => {
      navigate(".");

      return await paginasWebQuery.refetch();
    },
  });

  const paginaWebVerifyMutation = useMutation({
    mutationKey: ["empresa:verify:pagina-web"],
    mutationFn: (data) =>
      toast.promise(empresasService.verifyPaginaWeb(data), {
        loading: "Verificando la dirección de página web...",
        success: (response) => response.statusText,
        error: (error) => error.response.statusText,
      }),
    onSuccess: async () => {
      navigate(".");

      return await paginasWebQuery.refetch();
    },
  });

  async function addPaginaWeb({ paginaWeb }) {
    return await paginaWebAddMutation.mutateAsync({
      empresaId: params.empresa_id,
      paginaWeb,
    });
  }

  async function updatePaginaWeb({ paginaWebId, paginaWeb }) {
    const { pagina_web_verificada, ...rest } = paginaWeb;

    return await paginaWebUpdateMutation.mutateAsync({
      empresaId: params.empresa_id,
      paginaWebId,
      paginaWeb: rest,
    });
  }

  async function deletePaginaWeb({ paginaWebId }) {
    const deleted = window.confirm(
      "¿Desea eliminar esta dirección de página web?"
    );

    if (!!deleted) {
      await paginaWebDeleteMutation.mutateAsync({
        empresaId: params.empresa_id,
        paginaWebId,
      });

      return true;
    }

    return false;
  }

  async function verifyPaginaWeb({ paginaWebId }) {
    const confirmed = window.confirm(
      "¿Desea verificar esta dirección de página web?"
    );

    if (confirmed) {
      return await paginaWebVerifyMutation.mutateAsync({
        empresaId: params.empresa_id,
        paginaWebId,
      });
    }

    return false;
  }

  async function cancel() {
    if (window.confirm("¿Desea cancelar la edición?")) {
      return navigate(".");
    }
  }

  if (!!paginasWebQuery.isLoading) {
    return <Spinner label="Cargando las direcciones de página web..." />;
  } else if (!!paginasWebQuery.isError) {
    return <ErrorMessage message={paginasWebQuery.error.response.statusText} />;
  }

  return (
    <PaginasWebContext.Provider
      value={{
        paginasWeb: paginasWebQuery.data,
        isLoading: paginasWebQuery.isLoading,
        isFetching: paginasWebQuery.isFetching,
        isRefetching: paginasWebQuery.isRefetching,
        addPaginaWeb,
        isAdding: paginaWebAddMutation.isLoading,
        updatePaginaWeb,
        isUpdating: paginaWebUpdateMutation.isLoading,
        deletePaginaWeb,
        isDeleting: paginaWebDeleteMutation.isLoading,
        verifyPaginaWeb,
        isVerifing: paginaWebVerifyMutation.isLoading,
        cancel,
      }}
    >
      {children}
    </PaginasWebContext.Provider>
  );
}

export function usePaginasWebContext() {
  const paginasWebContext = useContext(PaginasWebContext);

  if (!paginasWebContext) {
    throw new Error(
      "usePaginasWebContext should be used inside the PaginasWebProvider."
    );
  }

  return paginasWebContext;
}
